import { IQuoteBasicRespSchema } from '@monetize/types/app';
import { Fragment, memo, useLayoutEffect, useRef } from 'react';
import { MdChevronRight } from 'react-icons/md';
import { Link } from 'react-router-dom';
import { ContractStatusTag } from '../../../components/Contracts/Lineage/ContractStatusTag';
import { ContractTermChip } from '../../../components/Contracts/Lineage/ContractTermChip';
import { RenewalQuoteStack } from '../../../components/Contracts/Lineage/RenewalQuoteStack';
import {
  MBox,
  MCard,
  MCardBody,
  MCardHeader,
  MFlex,
  MIcon,
  MLink,
  MText,
} from '../../../components/Monetize';
import { getBillGroupPageRoute } from '../../../constants/routes';
import { ContractLineageWithBillGroups } from '../../../hooks/useContractLineages';
import {
  getCurrentLineageContract,
  getLineageNextEvent,
} from '../../../utils/contractLineage';
import { BillGroupInvoicingMenu } from './BillGroupInvoicingMenu';

interface ContractLineageCardProps {
  lineage: ContractLineageWithBillGroups;
  today: Date;
  /** Omit when the user cannot open contracts; terms render disabled */
  onSelectContract?: (contractId: string) => void;
  /** Shows the invoicing menu for the current term's bill group */
  canManageInvoices?: boolean;
  /** Renewal quotes not yet processed into a term, shown after the last term */
  renewalQuotes?: IQuoteBasicRespSchema[];
}

const TermConnector = () => (
  <MFlex
    aria-hidden
    align="center"
    w="32px"
    flexShrink={0}
    color="border.strong"
  >
    <MBox flex={1} h="2px" bg="border.default" />
    <MIcon as={MdChevronRight} boxSize={4} ml="-6px" />
  </MFlex>
);

/**
 * One lineage: header summarising the current term, then every term
 * left → right. Single-term lineages use the same layout so chips line up
 * across cards.
 */
export const ContractLineageCard = memo(
  ({
    lineage,
    today,
    onSelectContract,
    canManageInvoices,
    renewalQuotes,
  }: ContractLineageCardProps) => {
    const chainRef = useRef<HTMLDivElement>(null);
    const current = getCurrentLineageContract(lineage);
    const termCount = lineage.contracts.length;
    const billGroupCount = lineage.billGroupIds.length;

    // Long chains scroll sideways; start with the current term centered
    useLayoutEffect(() => {
      const container = chainRef.current;
      const currentChip =
        container?.querySelector<HTMLElement>('[data-current]');
      if (
        container &&
        currentChip &&
        container.scrollWidth > container.clientWidth
      ) {
        container.scrollLeft =
          currentChip.offsetLeft -
          (container.clientWidth - currentChip.offsetWidth) / 2;
      }
    }, [lineage.currentContractId, termCount]);

    const summary = (
      <MFlex align="center" columnGap={4} rowGap={2} flexWrap="wrap" w="100%">
        <MBox minW={0}>
          {current.billGroup ? (
            <MLink asChild fontWeight="semibold" lineClamp={1}>
              <Link to={getBillGroupPageRoute(current.billGroupId)}>
                {current.billGroup.name}
              </Link>
            </MLink>
          ) : (
            <MText fontWeight="semibold" color="fg.strong">
              {current.billGroupId}
            </MText>
          )}
          {billGroupCount > 1 && (
            <MText fontSize="xs" color="fg.muted">
              Spans {billGroupCount} bill groups
            </MText>
          )}
        </MBox>
        <MFlex
          align="center"
          gap={2}
          flexWrap="wrap"
          fontSize="xs"
          color="fg.muted"
        >
          <ContractStatusTag status={current.status} />
          <MText as="span" fontSize="inherit" color="inherit">
            {getLineageNextEvent(lineage, today)}
          </MText>
        </MFlex>
        {canManageInvoices && (
          <MBox ml="auto">
            <BillGroupInvoicingMenu
              accountId={current.accountId}
              billGroupId={current.billGroupId}
              testIdSuffix={lineage.id}
            />
          </MBox>
        )}
      </MFlex>
    );

    return (
      <MCard variant="edit" data-testid={`contract-lineage-card-${lineage.id}`}>
        <MCardHeader>{summary}</MCardHeader>
        <MCardBody p={0}>
          <MBox
            ref={chainRef}
            position="relative"
            overflowX="auto"
            px={4}
            py={3.5}
          >
            <MFlex align="center" minW="max-content">
              {lineage.contracts.map((contract, index) => (
                <Fragment key={contract.id}>
                  {index > 0 && <TermConnector />}
                  <ContractTermChip
                    contract={contract}
                    termNumber={index + 1}
                    termCount={termCount}
                    isCurrent={contract.id === lineage.currentContractId}
                    isUpcoming={contract.id === lineage.upcomingContractId}
                    onSelect={onSelectContract}
                  />
                </Fragment>
              ))}
              {!!renewalQuotes?.length && (
                <>
                  <TermConnector />
                  <RenewalQuoteStack
                    quotes={renewalQuotes}
                    testIdSuffix={lineage.id}
                  />
                </>
              )}
            </MFlex>
          </MBox>
        </MCardBody>
      </MCard>
    );
  },
);
